<template>
    <div class="CampaignHistoryTranscription">
        <div v-if="isLoading" class="loading-box centerXY">
            <v-progress-circular
                indeterminate
                color="primary"
                style="width: 26px"
                class="mr-8"
            ></v-progress-circular>
            {{ $f.tc("loadingData") }}
        </div>
        <div
            v-else
            class="message-scroll px-12 py-12 fci-scrollbar"
            ref="MessageScroll"
        >
            <div
                v-if="messageList.length === 0"
                class="centerY" style="flex-direction: column; margin-top: 100px; font-family: 'open sans',serif"
            >
                <img src="/img/debug/not-found.svg" alt="icon-not-found-call" style="width: 180px">
                <span style="color: #545253; font-size: 16px; margin-top: -12px; font-family: 'open sans semi',serif">
                    {{ $f.tc('noDataAvailable') }}
                </span>
                <span style="color: #9a9a9a; font-size: 13px;">{{ $f.tc('notFoundCall') }}</span>
            </div>
            <div
                v-for="(item, j) in messageList"
                :key="j"
                :style="[j !== 0 ? { marginTop: '12px' } : '']"
            >
                <div v-if="item.type === 'user'" class="message-container user">
                    <div class="message user" style="max-width: 70%">
                        {{ item.content }}
                    </div>
                </div>
                <div v-else class="message-container bot">
                    <div
                        style="width: 40px"
                        v-if="j > 0 && messageList[j - 1].type === 'bot'"
                    ></div>
                    <div class="message-icon-container" v-else>
                        <img src="/img/scenario/process_active.svg" alt="bot" />
                    </div>
                    <div style="max-width: 70%">
                        <div
                            class="message bot mx-2"
                            style="flex-direction: column;"
                            v-if="canPlayAudio(item.audioLink)"
                        >
                            <div
                                class="centerY mb-6 align-self-start"
                                style="flex-wrap: wrap"
                            >
                                <strong class="pr-6"
                                >{{ $f.tc("audioName") }}:</strong
                                >
                                {{
                                    item.audioName ? item.audioName : "unknown"
                                }}
                            </div>
                            <audio
                                :src="item.audioLink"
                                controls="controls"
                                class="audio-custom"
                            ></audio>
                        </div>
                        <div class="message bot mx-2" v-else>
                            {{ item.content }}
                        </div>
                    </div>
                </div>
                <div v-if="item.nlp" class="nlp-container">
                    <span
                        v-for="(entity, entityIndex) in item.nlp.entities"
                        :key="entityIndex"
                    >
                        ( {{ entity.entity }} - {{ entity.value }} )
                    </span>
                    <span style="color: red" class="mr-2">
                        {{ item.nlp.intent ? item.nlp.intent.label : "" }}
                    </span>
                    <span style="color: #0052CC">
                        {{ item.nlp.intent ? Math.round(item.nlp.intent.confidence * 100) : 100 }}%
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import lodash from 'lodash'
import { mapState } from 'vuex'
import CampaignService from '@/services/campaign.service'

export default {
    name: "CampaignHistoryTranscription",
    props: {
        activeCallId: {
            type: String,
            default: () => ""
        },
    },
    data() {
        return {
            messageList: [],
            isLoading: false,
        }
    },
    computed: {
        ...mapState({
            campaign: state => state.campaign.campaign,
        }),
        callId() {
            return this.$route.query.call_id || ''
        }
    },
    mounted () {
        this.getHistoryCall()
    },
    methods: {
        close() {
            let query = this.$utils.cloneObject(this.$route.query)
            delete query.call_id
            delete query.from
            delete query.to
            if (!lodash.isEqual(query, this.$route.query)) {
                this.$router.replace({
                    query: query
                })
            }
        },
        getHistoryCall() {
            this.isLoading = true
            this.messageList = []
            CampaignService.getHistoryCall(this.campaign.id, this.callId).then(
                result => {
                    let list = []
                    let logList = lodash.orderBy(result.data, '_source.created_time', 'desc')
                    let decisionNode = ''
                    let nextNode = null
                    for (let log of logList) {
                        //bot say
                        const detailMessage = log['_source'].input.message
                        let output = Array.isArray(log['_source'].output) ? log['_source'].output[0] : log['_source'].output
                        let audioLink = output['audio_link'] ? output['audio_link'] : ''
                        let audioSlug = output['audio_slug'] ? output['audio_slug'] : ''
                        let audioName = output['audio_name'] ? output['audio_name'] : ''

                        // case bot transfer agent
                        let nextActionType = output.next_action?.type
                        if (nextActionType === 'transfer_call') {
                            list.push({
                                type: 'bot',
                                content: `-----${nextActionType}-----`,
                                decisionNode: decisionNode,
                                audioLink: audioLink,
                                audioSlug: audioSlug,
                                audioName: audioName,
                                nextNode: nextNode,
                            })
                        }

                        // case bot say normal
                        let botSay = {
                            type: 'bot',
                            content: Array.isArray(log['_source'].output) ? log['_source'].output[0].prompt : log['_source'].output.prompt,
                            decisionNode: decisionNode,
                            audioLink: audioLink,
                            audioSlug: audioSlug,
                            audioName: audioName,
                            nextNode: nextNode,
                        }
                        if (botSay.content !== '_NOT_SHOWN_') {
                            list.push(botSay)
                        }

                        // user say
                        let userSay = {
                            type: 'user',
                            user_attributes: log['_source']['user_attribute'],
                            input: log['_source']['input'],
                        }

                        if (detailMessage.type === 'payload') {
                            nextNode = {
                                'next_node_id': log['_source']['nodes_flow'] ? log['_source']['nodes_flow']['to_node']['id'] : '',
                                'next_node_slug': log['_source']['nodes_flow'] ? log['_source']['nodes_flow']['to_node']['slug'] : '',
                            }
                            if (detailMessage.content === 'welcome') {
                                userSay.content = '-----new conversation-----'
                                list.push(userSay)
                                break
                            }

                            if (detailMessage.content === 'get_more_prompt') {
                                continue
                            }
                        } else if (detailMessage.type === 'end_call') {
                            // call has end_call but chat test does not
                            userSay.content = '-----end call-----'
                            list.shift()
                        } else {
                            nextNode = {
                                'next_node_id': log['_source']['nodes_flow']?.middle_node?.id || '',
                                'next_node_slug': log['_source']['nodes_flow']?.middle_node?.slug || '',
                            }
                            userSay.content = detailMessage.content
                            const nlpResponse = log['_source']['nlp_response']
                            const checkIntentNlp = nlpResponse && nlpResponse.data.intents && nlpResponse.data.intents[0]
                            userSay.nlp = {
                                intent: checkIntentNlp ? nlpResponse.data.intents[0] : null,
                                entities: nlpResponse ? nlpResponse.data.entities : []
                            }
                        }

                        list.push(userSay)
                    }
                    this.$emit("updateCallSummary", logList[0]['_source']['user_attribute']['$callSummary'])
                    this.messageList = list.reverse()
                }
            ).finally(() => this.isLoading = false)
        },
        validURL (str) {
            let pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
            return !!pattern.test(str)
        },
        canPlayAudio (audioLink) {
            if (this.validURL(audioLink)) {
                let ext = audioLink.split(/[#?]/)[0].split('.').pop().trim()
                if (ext === 'wav' || ext === 'mp3') {
                    return true
                }
            }
            return false
        }
    },
    watch: {
        callId: 'getHistoryCall'
    }
}
</script>

<style scoped lang="scss">
@import "../Scenario/chat.scss";

.CampaignHistoryTranscription {
    border-left: 1px solid #d9d9d9;
    flex: 1;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #f0f0f0;
        height: 58px;
        padding: 0 5px 0 12px;
    }

    .title {
        font-size: 16px !important;
        font-family: "open sans bold", serif !important;
    }

    .subtitle {
        margin-top: 6px;
        font-size: 13px;
    }
}

.message-scroll,
.loading-box {
    height: calc(100vh - 295px);
    overflow: auto;
}

.loading-box {
    font-size: 14px;
    font-family: "open sans", serif;
}

.nlp-container {
    text-align: right;
    font-size: 12px;
    margin-right: 10px;
    margin-top: 3px;
}
</style>
